import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, ChevronLeft,
  ArrowRight, Mic, Upload, CheckCircle2, Loader2, RotateCcw, Wrench, Sparkles,
} from "lucide-react";
import { BrandLink } from "@/components/Brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Short, scannable labels — what the technician should say at each stage.
const LABELS = {
  "01-greet":                 { title: "Greet the customer",     colour: "primary" },
  "02-job-overview":          { title: "Explain today's work",   colour: "primary" },
  "03-aircon-find":           { title: "Air-con — gas low",      colour: "warning" },
  "04-aircon-decontam":       { title: "Vent decontamination",   colour: "warning" },
  "05-front-tyres":           { title: "Front tyres — good",     colour: "success" },
  "06-front-brakes-suspension":{ title: "Front brakes & suspension", colour: "success" },
  "07-underside":             { title: "Underside — all good",   colour: "success" },
  "08-undertrays":            { title: "Undertrays secure",      colour: "success" },
  "09-rear-tyres":            { title: "Rear tyres — advisory",  colour: "warning" },
  "10-rear-brakes":           { title: "Rear brakes — red",      colour: "destructive" },
  "11-lights-levels":         { title: "Lights & levels",        colour: "success" },
  "12-wipers":                { title: "Wiper blades",           colour: "warning" },
  "13-horn-mot":              { title: "Horn, dash, MOT",        colour: "success" },
  "14-thanks":                { title: "Thank you & close",      colour: "primary" },
};

export default function TechStandardsPage() {
  const [stages, setStages] = useState([]);
  const [voice, setVoice] = useState("echo");
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [audioReady, setAudioReady] = useState(false);
  const [sources, setSources] = useState({});
  const [uploading, setUploading] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/tech-standards/stages`).then((r) => {
      setStages(r.data.stages || []);
      setVoice(r.data.voice || "echo");
    }).catch(() => toast.error("Couldn't load technician standards"));
  }, []);

  useEffect(() => {
    if (!stages.length) return;
    const stage = stages[idx];
    if (!stage) return;
    setAudioReady(false);
    setProgress(0);
    axios.get(`${API}/tech-standards/audio/${stage.id}/source`).then((r) => {
      setSources((s) => ({ ...s, [stage.id]: r.data.source }));
    }).catch(() => {});
    // Stamp the user as "watched" so the onboarding checklist ticks step 5.
    // Best-effort — we don't block UI.
    const token = localStorage.getItem("token");
    if (token) {
      axios.post(`${API}/onboarding/mark-watched`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    const a = audioRef.current;
    if (a) {
      a.src = `${API}/tech-standards/audio/${stage.id}`;
      a.load();
    }
  }, [idx, stages]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.play().catch(() => setPlaying(false));
    else a.pause();
  }, [playing]);

  const onTime = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setProgress((a.currentTime / a.duration) * 100);
  };
  const onEnded = () => {
    if (idx < stages.length - 1) { setIdx(idx + 1); setPlaying(true); }
    else { setPlaying(false); setProgress(100); }
  };
  const onCanPlay = () => setAudioReady(true);
  const jumpTo = (i) => {
    if (i < 0 || i >= stages.length) return;
    setIdx(i); setPlaying(false); setProgress(0);
  };

  const handleUpload = async (file) => {
    const stage = stages[idx];
    if (!stage || !file) return;
    setUploading(stage.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token");
      await axios.post(`${API}/tech-standards/audio/${stage.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      setSources((s) => ({ ...s, [stage.id]: "custom" }));
      const a = audioRef.current;
      if (a) { a.src = `${API}/tech-standards/audio/${stage.id}?t=${Date.now()}`; a.load(); }
      toast.success("Recording uploaded");
    } catch {
      toast.error("Couldn't upload — sign in first?");
    } finally { setUploading(null); }
  };

  const revertToAi = async () => {
    const stage = stages[idx];
    if (!stage) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/tech-standards/audio/${stage.id}/custom`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSources((s) => ({ ...s, [stage.id]: "ai" }));
      const a = audioRef.current;
      if (a) { a.src = `${API}/tech-standards/audio/${stage.id}?t=${Date.now()}`; a.load(); }
      toast.success("Reverted to AI voice");
    } catch { toast.error("Couldn't revert"); }
  };

  const stage = stages[idx];
  const totalDuration = stages.reduce((s, x) => s + (x.duration_estimate || 25), 0);
  const currentSource = stage ? sources[stage.id] : "ai";
  const colour = (LABELS[stage?.id]?.colour) || "primary";

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="tech-standards-page">
      <audio ref={audioRef} onTimeUpdate={onTime} onEnded={onEnded} onCanPlay={onCanPlay} muted={muted} />

      <header className="border-b border-white/10 backdrop-blur sticky top-0 z-30 bg-black/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <BrandLink />
          <div className="flex items-center gap-2">
            <Link to="/walkthrough" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/15 hover:border-primary/50 text-xs font-semibold text-white/80 hover:text-white transition-colors" data-testid="tech-standards-manager-link">
              <Sparkles className="w-3.5 h-3.5" /> Manager walkthrough
            </Link>
            <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/15 hover:border-white/40 text-xs font-semibold text-white/80 hover:text-white transition-colors" data-testid="tech-standards-home-link">
              <ChevronLeft className="w-3.5 h-3.5" /> Home
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="text-center mb-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary mb-3 inline-flex items-center gap-2">
            <span className="w-6 h-px bg-primary" /> Technician video standards <span className="w-6 h-px bg-primary" />
          </div>
          <h1 className="font-display font-black tracking-tight text-3xl md:text-5xl leading-[0.95]">
            14 stages. <span className="text-primary">Every customer video.</span>
          </h1>
          <p className="mt-3 text-white/65 text-sm md:text-base max-w-xl mx-auto">
            The Toyota service-centre standard · ~{Math.ceil(totalDuration / 60)} min total · narrated by AI · tap any stage to jump in
          </p>
        </div>

        {stage && (
          <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur p-6 md:p-8 shadow-2xl" data-testid="tech-standards-player">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
                  Stage {String(idx + 1).padStart(2, "0")} / {String(stages.length).padStart(2, "0")}
                </div>
                <h2 className="font-display text-2xl md:text-4xl font-black mt-2 leading-tight">
                  {LABELS[stage.id]?.title || stage.id}
                </h2>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                  colour === "success" ? "bg-success/15 border-success/30 text-success"
                  : colour === "warning" ? "bg-warning/15 border-warning/30 text-warning"
                  : colour === "destructive" ? "bg-destructive/15 border-destructive/30 text-destructive"
                  : "bg-primary/15 border-primary/30 text-primary"
                }`}>
                  <Wrench className="w-3 h-3" /> {colour === "destructive" ? "RED" : colour === "warning" ? "AMBER" : colour === "success" ? "GREEN" : "INFO"}
                </span>
                {currentSource === "custom" ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-warning/15 border border-warning/30 text-warning text-[10px] font-bold uppercase tracking-wider" data-testid="tech-audio-source-custom">
                    <Mic className="w-3 h-3" /> Your voice
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/15 border border-primary/30 text-primary text-[10px] font-bold uppercase tracking-wider" data-testid="tech-audio-source-ai">
                    <Sparkles className="w-3 h-3" /> AI voice
                  </span>
                )}
              </div>
            </div>

            <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed font-light" data-testid="tech-standards-transcript">
              {stage.text}
            </p>

            <div className="mt-6 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <button onClick={() => jumpTo(idx - 1)} disabled={idx === 0} className="p-2.5 rounded-full border border-white/15 hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="tech-standards-prev" aria-label="Previous stage">
                  <SkipBack className="w-4 h-4" />
                </button>
                <button onClick={() => setPlaying((p) => !p)} disabled={!audioReady} className="p-4 rounded-full bg-primary text-primary-foreground hover:scale-105 active:scale-95 transition-transform disabled:opacity-40" data-testid="tech-standards-play" aria-label={playing ? "Pause" : "Play"}>
                  {!audioReady ? <Loader2 className="w-5 h-5 animate-spin" /> : playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5" fill="currentColor" />}
                </button>
                <button onClick={() => jumpTo(idx + 1)} disabled={idx >= stages.length - 1} className="p-2.5 rounded-full border border-white/15 hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="tech-standards-next" aria-label="Next stage">
                  <SkipForward className="w-4 h-4" />
                </button>
                <button onClick={() => setMuted((m) => !m)} className="p-2.5 rounded-full border border-white/15 hover:border-white/40 transition-colors ml-1" data-testid="tech-standards-mute" aria-label={muted ? "Unmute" : "Mute"}>
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              </div>

              <div className="flex items-center gap-2 text-xs">
                {currentSource === "custom" && (
                  <button onClick={revertToAi} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/15 hover:border-white/40 text-white/80 hover:text-white transition-colors" data-testid="tech-standards-revert-ai">
                    <RotateCcw className="w-3.5 h-3.5" /> Revert to AI
                  </button>
                )}
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/15 hover:border-primary/50 cursor-pointer text-white/80 hover:text-white transition-colors" data-testid="tech-standards-upload-label">
                  {uploading === stage.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {currentSource === "custom" ? "Replace recording" : "Record over · upload your voice"}
                  <input type="file" accept="audio/*,.mp3,.m4a,.wav" className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                    data-testid="tech-standards-upload-input" />
                </label>
              </div>
            </div>
          </section>
        )}

        <section className="mt-10" data-testid="tech-standards-timeline">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">Jump to any stage</div>
          <ol className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {stages.map((s, i) => {
              const active = i === idx;
              const done = i < idx;
              const label = LABELS[s.id]?.title || s.id;
              const c = LABELS[s.id]?.colour || "primary";
              return (
                <li key={s.id}>
                  <button
                    onClick={() => jumpTo(i)}
                    className={`w-full text-left rounded-lg border p-3 text-xs transition-all ${
                      active
                        ? "bg-primary/15 border-primary text-white shadow-[0_0_24px_-8px_rgba(99,255,170,0.4)]"
                        : "bg-white/[0.02] border-white/10 text-white/70 hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`tech-standards-step-${i}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`font-mono text-[10px] ${active ? "text-primary" : "text-white/40"}`}>{String(i + 1).padStart(2, "0")}</span>
                      {done && <CheckCircle2 className="w-3 h-3 text-success" />}
                      <span className={`ml-auto w-1.5 h-1.5 rounded-full ${
                        c === "success" ? "bg-success" : c === "warning" ? "bg-warning"
                        : c === "destructive" ? "bg-destructive" : "bg-primary"
                      }`} />
                    </div>
                    <div className="font-semibold mt-1 truncate">{label}</div>
                  </button>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="mt-12 rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8 text-center" data-testid="tech-standards-cta">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-primary mb-2">For new technicians</div>
          <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight">
            Every video. Every time. Same standard.
          </h3>
          <p className="text-white/65 text-sm md:text-base mt-2 max-w-xl mx-auto">
            Use this as the script for every customer video — or record your own voice over the top and make it personal.
          </p>
          <Link to="/dashboard" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-bold hover:scale-[1.02] transition-transform" data-testid="tech-standards-cta-btn">
            Back to the dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <footer className="border-t border-white/10 py-6 text-center text-[11px] text-white/40">
        ServiceSellPro Limited · Company No. 17211707 · ICO ZC144538 · Voice: {voice} (changeable per stage)
      </footer>
    </div>
  );
}
